import config from '../config';
import {
  customFetch,
  authHeader,
  jsonHeader,
  handleResponse,
  commonRequestOptions,
  subjects,
} from '../helpers';

const endpointPath = config.api.API_URL + '/dataset';

export const datasetService = {
  getAll,
  getById,
  getAncestors,
  getByDatasetGroupId,
  getByDatasourceId,
  getFieldStatistics,
  create,
  update,
  checkDestroy,
  destroy,
};

function getAll(signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/all`, requestOptions, signal).then(handleResponse);
}

function getById(id, signal, setCurrentDataset = true) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  const datasetRequest = customFetch(`${endpointPath}/${id}`, requestOptions, signal).then(
    handleResponse
  );

  // Callers that don't drive the breadcrumb (e.g. dataset preview popups) just get the dataset.
  if (!setCurrentDataset) return datasetRequest;

  // Fetch the dataset and its ancestor chain together and publish currentDataset and
  // currentDatasetAncestors in the same render. Doing both in one update is what prevents the
  // breadcrumb parent crumbs from flashing in a step after the dataset crumb. The ancestors fetch
  // is non-critical, so its failure must not reject the dataset load (a rejecting dataset request
  // still propagates, preserving the view's not-found handling).
  return Promise.all([
    datasetRequest,
    getAncestors(id, signal).catch(() => ({ datasets: [] })),
  ]).then(([res, ancestorsRes]) => {
    subjects.currentDataset.next(res.datasets[0] || { dataset_id: id });
    subjects.currentDatasetGroup.next(
      res.datasets[0]
        ? {
            dataset_group_id: res.datasets[0].dataset_group_id,
            dataset_group_name: res.datasets[0].dataset_group_name,
            datasource_hostname: res.datasets[0].datasource_hostname,
            datasource_database: res.datasets[0].datasource_database,
            datasource_type_name: res.datasets[0].datasource_type_name,
            datasource_type_code: res.datasets[0].datasource_type_code,
            datasource_type_category: res.datasets[0].datasource_type_category,
          }
        : 'not_found'
    );
    subjects.currentDatasource.next(
      res.datasets[0]
        ? {
            datasource_id: res.datasets[0].datasource_id,
            datasource_name: res.datasets[0].datasource_name,
            datasource_hostname: res.datasets[0].datasource_hostname,
            datasource_database: res.datasets[0].datasource_database,
            datasource_type_name: res.datasets[0].datasource_type_name,
            datasource_type_code: res.datasets[0].datasource_type_code,
            datasource_type_category: res.datasets[0].datasource_type_category,
          }
        : 'not_found'
    );
    subjects.currentSystem.next(
      res.datasets[0]
        ? {
            system_id: res.datasets[0].system_id,
            system_name: res.datasets[0].system_name,
          }
        : 'not_found'
    );
    subjects.currentDatasetAncestors.next({ dataset_id: id, ancestors: ancestorsRes.datasets });
    return res;
  });
}

/**
 * Sends a GET request for a dataset's ancestor chain (parent datasets), ordered
 * root -> immediate parent. Returns an empty list for a top-level dataset.
 * @param {number|string} id - The dataset id.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ datasets: object[] }>}
 */
function getAncestors(id, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/${id}/ancestors`, requestOptions, signal).then(
    handleResponse
  );
}

function getByDatasetGroupId(id, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/dataset_group/${id}`, requestOptions, signal).then(
    handleResponse
  );
}

function getByDatasourceId(id, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/datasource/${id}`, requestOptions, signal).then(
    handleResponse
  );
}

/**
 * Sends a GET request for the field statistics of every dataset in a dataset group.
 *
 * Each row carries both a `field_count` rolled up over the dataset's subtree and the
 * `own_field_count` it holds directly — the latter is what decides whether a dataset offers to
 * open, since a dataset whose fields all sit in its children would otherwise open onto nothing.
 *
 * @param {number|string} id - Dataset group id.
 * @param {AbortSignal} [signal]
 * @returns {Promise<{ field_statistics: Array<object> }>}
 */
function getFieldStatistics(id, signal) {
  const requestOptions = { method: 'GET', headers: authHeader(), ...commonRequestOptions() };
  return customFetch(`${endpointPath}/field_statistics/${id}`, requestOptions, signal).then(
    handleResponse
  );
}

function create(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function update(items, signal) {
  const requestOptions = {
    method: 'PUT',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}

function checkDestroy(items, signal) {
  const requestOptions = {
    method: 'POST',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}/check_delete`, requestOptions, signal).then(handleResponse);
}

function destroy(items, signal) {
  const requestOptions = {
    method: 'DELETE',
    headers: {
      ...authHeader(),
      ...jsonHeader(),
    },
    ...commonRequestOptions(),
    body: JSON.stringify(items),
  };

  return customFetch(`${endpointPath}`, requestOptions, signal).then(handleResponse);
}
